import React, { useEffect, useRef, useState } from "react";
import whiteLogo from "../../assets/icons/white_logo_png.png";

// Ordered deploy phases per tech (mirrors SmartHub's DEPLOY_STAGE_MAP) — seeds the
// timeline so it's visible before/independent of backend `deployment:stages` events.
const STAGE_MAP = {
  mule:       ["Anypoint Exchange", "GitHub", "Azure Pipeline"],
  springboot: ["GitHub", "Azure Pipeline"],
  dotnet:     ["GitHub", "Azure Pipeline"],
};

const C = {
  panel: "#121825", bg: "#0e1219", border: "rgba(255,255,255,0.08)",
  accent: "#3b7fff", text: "#dce8ff", sub: "#aac0dc", muted: "#7a93bc",
  good: "#00c98d", warn: "#f5a623", bad: "#ff6b6b",
};

// Braille spinner (no CSS keyframes needed) for the active phase.
function Spinner() {
  const frames = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % frames.length), 90); return () => clearInterval(t); }, []); // eslint-disable-line
  return <span style={{ color: C.accent, fontWeight: 700 }}>{frames[i]}</span>;
}

function statusIcon(status, isPauseHere) {
  if (isPauseHere) return <span style={{ color: C.warn }}>⏸</span>;
  if (status === "active") return <Spinner />;
  if (status === "done") return <span style={{ color: C.good }}>✓</span>;
  if (status === "error") return <span style={{ color: C.bad }}>✗</span>;
  return <span style={{ color: C.muted }}>○</span>;
}

function stateLabel(status) {
  return status === "active" ? "working…" : status === "done" ? "done" : status === "error" ? "failed" : "waiting";
}

export default function DeploymentAgentConsole({ tech, stages, pipelineStages, logs, confirmation, onProceed, onCancel, onReset }) {
  const logEndRef = useRef(null);
  const [open, setOpen] = useState({});     // per-phase expand
  const [showLog, setShowLog] = useState(true); // live log open by default (like SmartHub's terminal)

  useEffect(() => { if (showLog && logEndRef.current) logEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [logs, showLog]);

  // ── Phase list: the page owns accurate statuses (seeded + advanced from the log
  // stream, like SmartHub); render them as-is. Fall back to a pending seed only when
  // no stages have been provided yet. ──
  const phases = (Array.isArray(stages) && stages.length)
    ? stages
    : (STAGE_MAP[tech] || STAGE_MAP.mule).map((name) => ({ name, status: "pending" }));
  const activeIdx = phases.findIndex((p) => p.status === "active");
  const failed = phases.some((p) => p.status === "error");
  const done = phases.length > 0 && phases.every((p) => p.status === "done" || p.status === "error");
  const statusLabel = confirmation ? "paused" : failed ? "failed" : done ? "complete" : "deploying";
  const pillColor = statusLabel === "complete" ? C.good : statusLabel === "failed" ? C.bad : statusLabel === "paused" ? C.warn : C.accent;
  const nowLine = (logs && logs.length) ? logs[logs.length - 1].text : "Starting deployment…";
  const isAzure = (name) => name.toLowerCase().includes("azure") || name.toLowerCase().includes("pipeline");

  return (
    <div className="da-agent-console" style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.panel, overflow: "hidden" }}>
      {/* Agent header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderBottom: `1px solid ${C.border}`, background: "rgba(59,127,255,0.05)" }}>
        <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#0b0f17", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          <img src={whiteLogo} alt="agent" style={{ width: 22, height: 22, objectFit: "contain" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Deployment Agent</div>
          {!done && (
            <div style={{ fontSize: 12, color: C.sub, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ color: C.muted }}>Now:</span> {nowLine}
            </div>
          )}
        </div>
        <span className="da-ac-pill" style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: `${pillColor}22`, color: pillColor, textTransform: "capitalize", display: "inline-flex", alignItems: "center", gap: 6 }}>
          {!done && <span className="da-ac-dot" style={{ width: 7, height: 7, borderRadius: "50%", background: pillColor, display: "inline-block" }} />}
          {statusLabel}
        </span>
      </div>

      {/* Phase timeline */}
      <div style={{ padding: "10px 12px" }}>
        {phases.map((p, i) => {
          const pauseHere = !!confirmation && i === Math.max(activeIdx, 0);
          const azure = isAzure(p.name);
          const hasBody = azure && Array.isArray(pipelineStages) && pipelineStages.length > 0;
          const isOpen = open[p.name] ?? (p.status === "active" || p.status === "error");
          return (
            <div key={p.name} style={{ borderBottom: i < phases.length - 1 ? `1px solid rgba(255,255,255,0.04)` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 6px" }}>
                <span style={{ width: 18, textAlign: "center", fontSize: 13 }}>{statusIcon(p.status, pauseHere)}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: p.status === "pending" ? C.muted : C.text }}>{p.name}</span>
                <span style={{ fontSize: 11, color: p.status === "error" ? C.bad : p.status === "done" ? C.good : C.muted }}>
                  {pauseHere ? "awaiting you" : stateLabel(p.status)}
                </span>
                {hasBody && (
                  <button onClick={() => setOpen((o) => ({ ...o, [p.name]: !isOpen }))}
                    style={{ background: "transparent", border: "none", color: C.accent, cursor: "pointer", fontSize: 11 }}>
                    {isOpen ? "▾ logs" : "▸ logs"}
                  </button>
                )}
              </div>

              {/* Azure pipeline per-stage logs (previously dropped) */}
              {hasBody && isOpen && (
                <div style={{ margin: "0 6px 8px 34px", display: "flex", flexDirection: "column", gap: 6 }}>
                  {pipelineStages.map((ps, k) => (
                    <div key={k} style={{ border: `1px solid ${ps.failed ? "rgba(255,107,107,0.3)" : C.border}`, borderRadius: 7, overflow: "hidden" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: ps.failed ? C.bad : C.sub, padding: "5px 9px", background: C.bg }}>
                        {ps.failed ? "✗" : "•"} {ps.stage}
                      </div>
                      <pre className="da-scroll" style={{ margin: 0, padding: "6px 9px", fontSize: 11, lineHeight: 1.5, color: C.muted, background: "#0a0d13", maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap" }}>
                        {(ps.lines || []).join("\n")}
                      </pre>
                    </div>
                  ))}
                </div>
              )}

              {/* Inline confirmation card (agent is asking) */}
              {pauseHere && confirmation && (
                <div style={{ margin: "0 6px 10px 34px", border: `1px solid rgba(245,166,35,0.35)`, borderRadius: 8, background: "rgba(245,166,35,0.06)", padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.warn, marginBottom: 4 }}>⏸ The agent needs your confirmation</div>
                  {confirmation.message && <div style={{ fontSize: 12, color: C.text, marginBottom: 6, whiteSpace: "pre-wrap" }}>{confirmation.message}</div>}
                  {confirmation.details && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 11, color: C.sub, marginBottom: 8 }}>
                      {confirmation.details.assetId && <span>Asset: <strong>{confirmation.details.assetId}</strong></span>}
                      {confirmation.details.cloudhubUrl && <span>CloudHub: <a href={confirmation.details.cloudhubUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>{confirmation.details.cloudhubUrl}</a></span>}
                      {confirmation.details.repoUrl && <span>Repo: <a href={confirmation.details.repoUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>{confirmation.details.repoUrl}</a></span>}
                      {confirmation.details.branch && <span>Branch: <strong>{confirmation.details.branch}</strong></span>}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={onProceed} style={{ padding: "7px 16px", borderRadius: 7, border: "none", background: C.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>▶ Proceed</button>
                    {onCancel && <button onClick={onCancel} style={{ padding: "7px 14px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>✕ Cancel</button>}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Global live log */}
      <div style={{ borderTop: `1px solid ${C.border}` }}>
        <button onClick={() => setShowLog((v) => !v)} style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", color: C.sub, cursor: "pointer", fontSize: 11, fontWeight: 700, padding: "9px 16px" }}>
          {showLog ? "▾" : "▸"} Live log ({(logs || []).length})
        </button>
        {showLog && (
          <div className="da-scroll" style={{ maxHeight: 220, overflow: "auto", background: "#0a0d13", padding: "8px 14px", fontSize: 11, lineHeight: 1.6, fontFamily: "ui-monospace, Menlo, monospace" }}>
            {(logs || []).length === 0 && <div style={{ color: C.muted }}>Starting deployment…</div>}
            {(logs || []).map((l, i) => (
              <div key={i} style={{ color: l.type === "error" ? C.bad : l.type === "warning" ? C.warn : l.type === "success" ? C.good : C.muted }}>
                <span style={{ color: "#3d5273", marginRight: 8 }}>{l.timestamp}</span>{l.text}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>

      {/* Completion footer */}
      {done && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: failed ? C.bad : C.good, flex: 1 }}>
            {failed ? `Deployment failed at ${(phases.find((p) => p.status === "error") || {}).name || "a stage"}` : "Deployment complete 🎉"}
          </span>
          {onReset && <button onClick={onReset} style={{ padding: "7px 14px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.sub, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>↺ New deployment</button>}
        </div>
      )}
    </div>
  );
}
